import React, { FormEvent, useEffect, useState } from 'react';
import { ArrowRightIcon, CheckCircleIcon, MapPinIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../LanguageContext';

export type JoinRole = 'producer' | 'collector' | 'distributor' | 'logistic';
type JoinUsProps = { role: JoinRole };
type Coordinates = { latitude: number; longitude: number; accuracy: number };

const API_BASE = process.env.NODE_ENV === 'production'
  ? 'https://api-v2.rightoz.in/api/v1'
  : 'http://localhost:8081/api/v1';
const OTP_ENDPOINT = `${API_BASE}/join-us/otp`;
const JOIN_ENDPOINT = `${API_BASE}/join-us`;

export const normalizePhone = (value: string) => value.replace(/\D/g, '').slice(0, 10);
export const isSixDigitOtp = (value: string) => /^\d{6}$/.test(value);

const roleCopy = {
  en: {
    producer: ['Producer', 'Supply quality milk through a transparent collection network.'],
    collector: ['Collector', 'Help collect and record milk from partner producers.'],
    distributor: ['Distributor', 'Deliver traceable, fresh milk to local families.'],
    logistic: ['Logistics partner', 'Help move milk and containers safely through the network.'],
  },
  hi: {
    producer: ['उत्पादक', 'पारदर्शी संग्रह नेटवर्क के माध्यम से गुणवत्तापूर्ण दूध उपलब्ध कराएँ।'],
    collector: ['संग्रहकर्ता', 'साझेदार उत्पादकों से दूध एकत्र करने और रिकॉर्ड करने में मदद करें।'],
    distributor: ['वितरक', 'स्थानीय परिवारों तक ताज़ा और ट्रेस करने योग्य दूध पहुँचाएँ।'],
    logistic: ['लॉजिस्टिक्स पार्टनर', 'दूध और कंटेनरों को नेटवर्क में सुरक्षित रूप से पहुँचाने में मदद करें।'],
  },
} as const;

const copy = {
  en: {
    title: 'Join the Rightoz journey', intro: 'Share your details. This is a public interest form and does not create an account.',
    name: 'Name', phone: 'Phone number', applicantType: 'Applicant type', notes: 'Notes (optional)',
    state: 'State (optional)', city: 'City (optional)', namePlaceholder: 'Enter your full name',
    phonePlaceholder: '10-digit mobile number', notesPlaceholder: 'Tell us anything useful',
    statePlaceholder: 'Rajasthan', cityPlaceholder: 'Jaipur', location: 'Current location (optional)',
    useLocation: 'Use current location', locating: 'Detecting location…', locationCaptured: 'Location captured',
    locationDenied: 'Location permission was denied. You can continue without it.',
    locationUnavailable: 'Location is unavailable. You can continue without it.',
    httpsNote: 'Browser location requires HTTPS outside localhost.', sendOtp: 'Send OTP on WhatsApp', verify: 'Verify OTP and submit',
    otp: 'Six-digit OTP', otpHint: 'Enter the code sent to your phone.', resend: 'Resend OTP',
    resendIn: 'Resend in', changeDetails: 'Change details', required: 'Enter your name and phone number.',
    invalidPhone: 'Enter a valid 10-digit phone number.', invalidOtp: 'Enter a valid six-digit OTP.',
    invalidOrExpired: 'The OTP is invalid or expired. Please request a new code.',
    rateLimited: 'Too many OTP requests. Please wait before trying again.',
    networkError: 'Could not connect to the server. Please check your connection and try again.',
    genericError: 'We could not complete this request. Please try again.',
    success: 'Thank you. We have received your details and will contact you.',
    jaipur: 'Currently onboarding in Jaipur and nearby areas', waiting: 'Please wait…',
  },
  hi: {
    title: 'Rightoz के सफ़र से जुड़ें', intro: 'अपनी जानकारी साझा करें। यह सार्वजनिक रुचि फ़ॉर्म है और इससे कोई अकाउंट नहीं बनता।',
    name: 'नाम', phone: 'फ़ोन नंबर', applicantType: 'आवेदक का प्रकार', notes: 'नोट्स (वैकल्पिक)',
    state: 'राज्य (वैकल्पिक)', city: 'शहर (वैकल्पिक)', namePlaceholder: 'अपना पूरा नाम लिखें',
    phonePlaceholder: '10 अंकों का मोबाइल नंबर', notesPlaceholder: 'ज़रूरी जानकारी साझा करें',
    statePlaceholder: 'राजस्थान', cityPlaceholder: 'जयपुर', location: 'वर्तमान स्थान (वैकल्पिक)',
    useLocation: 'वर्तमान स्थान लें', locating: 'स्थान पता किया जा रहा है…', locationCaptured: 'स्थान मिल गया',
    locationDenied: 'स्थान की अनुमति नहीं मिली। आप इसके बिना आगे बढ़ सकते हैं।',
    locationUnavailable: 'स्थान उपलब्ध नहीं है। आप इसके बिना आगे बढ़ सकते हैं।',
    httpsNote: 'लोकलहोस्ट के बाहर ब्राउज़र लोकेशन के लिए HTTPS आवश्यक है।', sendOtp: 'WhatsApp पर OTP भेजें', verify: 'OTP सत्यापित करके भेजें',
    otp: 'छह अंकों का OTP', otpHint: 'अपने फ़ोन पर आया कोड दर्ज करें।', resend: 'OTP फिर भेजें',
    resendIn: 'फिर भेजें', changeDetails: 'जानकारी बदलें', required: 'नाम और फ़ोन नंबर दर्ज करें।',
    invalidPhone: 'सही 10 अंकों का फ़ोन नंबर दर्ज करें।', invalidOtp: 'सही छह अंकों का OTP दर्ज करें।',
    invalidOrExpired: 'OTP गलत है या समाप्त हो गया है। नया कोड माँगें।',
    rateLimited: 'बहुत अधिक OTP अनुरोध हुए हैं। कृपया कुछ देर बाद कोशिश करें।',
    networkError: 'सर्वर से संपर्क नहीं हो सका। इंटरनेट कनेक्शन जाँचकर फिर कोशिश करें।',
    genericError: 'अनुरोध पूरा नहीं हो सका। कृपया फिर कोशिश करें।',
    success: 'धन्यवाद। हमें आपकी जानकारी मिल गई है और हम आपसे संपर्क करेंगे।',
    jaipur: 'फिलहाल जयपुर और आसपास के क्षेत्रों में जुड़ाव जारी है', waiting: 'कृपया प्रतीक्षा करें…',
  },
};

const readError = async (response: Response) => {
  const payload = await response.json().catch(() => null);
  const detail = payload?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg).filter(Boolean).join(', ');
  return typeof payload?.message === 'string' ? payload.message : '';
};

const JoinUs: React.FC<JoinUsProps> = ({ role }) => {
  const { language } = useLanguage();
  const t = copy[language];
  const [step, setStep] = useState<'details' | 'otp' | 'success'>('details');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [applicantType, setApplicantType] = useState<JoinRole>(role);
  const [notes, setNotes] = useState('');
  const [stateName, setStateName] = useState('');
  const [cityName, setCityName] = useState('');
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const [locationStatus, setLocationStatus] = useState<'idle' | 'loading' | 'captured' | 'denied' | 'unavailable'>('idle');
  const [otpId, setOtpId] = useState('');
  const [otp, setOtp] = useState('');
  const [retryAfter, setRetryAfter] = useState(0);
  const [expiresIn, setExpiresIn] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => setApplicantType(role), [role]);
  useEffect(() => {
    if (retryAfter <= 0) return undefined;
    const timer = window.setInterval(() => setRetryAfter((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [retryAfter]);

  const requestLocation = () => {
    setLocationStatus('loading');
    if (!navigator.geolocation) {
      setLocationStatus('unavailable');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setCoordinates({ latitude: coords.latitude, longitude: coords.longitude, accuracy: coords.accuracy });
        setLocationStatus('captured');
      },
      (locationError) => setLocationStatus(locationError.code === 1 ? 'denied' : 'unavailable'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  const sendOtp = async () => {
    setError('');
    if (!name.trim() || !phone) {
      setError(t.required);
      return;
    }
    if (!/^\d{10}$/.test(phone)) {
      setError(t.invalidPhone);
      return;
    }
    setBusy(true);
    try {
      const response = await fetch(OTP_ENDPOINT, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, applicant_type: applicantType, delivery_channel: 'whatsapp' }),
      });
      if (!response.ok) {
        const message = await readError(response);
        setError(response.status === 429 ? message || t.rateLimited : message || t.genericError);
        return;
      }
      const payload = await response.json();
      if (!payload?.success || !payload?.data?.otp_id) {
        setError(payload?.message || t.genericError);
        return;
      }
      setOtpId(payload.data.otp_id);
      setRetryAfter(Number(payload.data.retry_after_seconds) || 60);
      setExpiresIn(Number(payload.data.expires_in_seconds) || 300);
      setOtp('');
      setStep('otp');
    } catch {
      setError(t.networkError);
    } finally {
      setBusy(false);
    }
  };

  const verifyAndSubmit = async () => {
    setError('');
    if (!isSixDigitOtp(otp)) {
      setError(t.invalidOtp);
      return;
    }
    setBusy(true);
    try {
      const response = await fetch(JOIN_ENDPOINT, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          otp_id: otpId, otp_code: otp, name: name.trim(),
          state_name: stateName.trim() || null, city_name: cityName.trim() || null,
          detected_address: null, latitude: coordinates?.latitude ?? null,
          longitude: coordinates?.longitude ?? null,
          location_accuracy_meters: coordinates?.accuracy ?? null,
          notes: notes.trim() || null,
        }),
      });
      if (!response.ok) {
        const message = await readError(response);
        if (response.status === 429) setError(message || t.rateLimited);
        else if ([400, 401, 403, 422].includes(response.status)) setError(message || t.invalidOrExpired);
        else setError(message || t.genericError);
        return;
      }
      setStep('success');
    } catch {
      setError(t.networkError);
    } finally {
      setBusy(false);
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (step === 'details') void sendOtp();
    else void verifyAndSubmit();
  };

  const activeRole = roleCopy[language][applicantType];
  const showHttpsNote = typeof window !== 'undefined'
    && window.location.protocol !== 'https:'
    && !['localhost', '127.0.0.1'].includes(window.location.hostname);
  const inputClass = 'mt-2 w-full rounded-2xl border border-[rgba(27,94,32,0.15)] px-4 py-3 font-normal outline-none transition focus:border-[var(--color-forest)] focus:ring-2 focus:ring-[rgba(27,94,32,0.12)]';

  return (
    <main className="min-h-screen bg-[var(--color-cream)] pb-20 pt-28">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-start lg:px-8">
        <section className="rounded-[2rem] bg-[var(--color-forest)] p-7 text-white shadow-[0_24px_70px_rgba(27,94,32,0.18)] sm:p-10">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10"><UserGroupIcon className="h-7 w-7 text-[#d7e2d0]" /></div>
          <p className="mt-8 text-sm font-semibold uppercase tracking-[0.18em] text-[#b8cfaa]">{activeRole[0]}</p>
          <h1 className="mt-4 font-display text-4xl leading-tight text-[var(--color-cream)] sm:text-5xl">{t.title}</h1>
          <p className="mt-5 text-base leading-8 text-[#d7e2d0]">{activeRole[1]}</p>
          <p className="mt-8 flex items-center gap-3 text-sm text-[#d7e2d0]"><MapPinIcon className="h-5 w-5 text-[#cfe0c5]" />{t.jaipur}</p>
          <p className="mt-4 text-sm leading-6 text-[#b8cfaa]">{t.intro}</p>
        </section>

        <section className="rounded-[2rem] border border-[rgba(27,94,32,0.1)] bg-white p-6 shadow-[0_18px_50px_rgba(27,94,32,0.08)] sm:p-8">
          {step === 'success' ? (
            <div className="py-12 text-center"><CheckCircleIcon className="mx-auto h-14 w-14 text-[var(--color-forest)]" /><h2 className="mt-6 font-display text-3xl text-[var(--color-ink)]">{t.success}</h2></div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <h2 className="font-display text-3xl text-[var(--color-ink)]">{step === 'details' ? t.intro : t.otp}</h2>
              {step === 'details' ? (
                <>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="block text-sm font-semibold text-[var(--color-ink)]">{t.name}<input value={name} onChange={(event) => setName(event.target.value)} placeholder={t.namePlaceholder} autoComplete="name" required className={inputClass} /></label>
                    <label className="block text-sm font-semibold text-[var(--color-ink)]">{t.phone}<input value={phone} onChange={(event) => setPhone(normalizePhone(event.target.value))} placeholder={t.phonePlaceholder} inputMode="numeric" autoComplete="tel" required className={inputClass} /></label>
                  </div>
                  <label className="block text-sm font-semibold text-[var(--color-ink)]">{t.applicantType}<select value={applicantType} onChange={(event) => setApplicantType(event.target.value as JoinRole)} className={inputClass}>{(Object.keys(roleCopy[language]) as JoinRole[]).map((type) => <option key={type} value={type}>{roleCopy[language][type][0]}</option>)}</select></label>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="block text-sm font-semibold text-[var(--color-ink)]">{t.state}<input value={stateName} onChange={(event) => setStateName(event.target.value)} placeholder={t.statePlaceholder} autoComplete="address-level1" className={inputClass} /></label>
                    <label className="block text-sm font-semibold text-[var(--color-ink)]">{t.city}<input value={cityName} onChange={(event) => setCityName(event.target.value)} placeholder={t.cityPlaceholder} autoComplete="address-level2" className={inputClass} /></label>
                  </div>
                  <label className="block text-sm font-semibold text-[var(--color-ink)]">{t.notes}<textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder={t.notesPlaceholder} rows={3} className={inputClass} /></label>
                  <div className="rounded-2xl border border-[rgba(27,94,32,0.12)] bg-[var(--color-cream)] p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3"><span className="text-sm font-semibold text-[var(--color-ink)]">{t.location}</span><button type="button" onClick={requestLocation} disabled={locationStatus === 'loading'} className="rounded-full border border-[rgba(27,94,32,0.2)] bg-white px-4 py-2 text-sm font-semibold text-[var(--color-forest)] disabled:opacity-60">{locationStatus === 'loading' ? t.locating : t.useLocation}</button></div>
                    {locationStatus === 'captured' && <p className="mt-3 text-sm text-[var(--color-forest)]">✓ {t.locationCaptured} ({Math.round(coordinates?.accuracy || 0)} m)</p>}
                    {locationStatus === 'denied' && <p className="mt-3 text-sm text-[#8a5a1f]">{t.locationDenied}</p>}
                    {locationStatus === 'unavailable' && <p className="mt-3 text-sm text-[#8a5a1f]">{t.locationUnavailable}</p>}
                    {showHttpsNote && <p className="mt-2 text-xs text-[var(--color-muted)]">{t.httpsNote}</p>}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-[var(--color-muted)]">{t.otpHint} {expiresIn > 0 && `(${Math.ceil(expiresIn / 60)} min)`}</p>
                  <label className="block text-sm font-semibold text-[var(--color-ink)]">{t.otp}<input value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="••••••" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus className={`${inputClass} text-center text-2xl tracking-[0.45em]`} /></label>
                  <div className="flex flex-wrap items-center justify-between gap-3 text-sm"><button type="button" onClick={() => void sendOtp()} disabled={busy || retryAfter > 0} className="font-semibold text-[var(--color-forest)] disabled:text-[var(--color-muted)]">{retryAfter > 0 ? `${t.resendIn} ${retryAfter}s` : t.resend}</button><button type="button" onClick={() => { setStep('details'); setOtp(''); setError(''); }} className="font-semibold text-[var(--color-forest)]">{t.changeDetails}</button></div>
                </>
              )}
              {error && <p role="alert" className="rounded-2xl bg-[#fff2ef] px-4 py-3 text-sm text-[#9c3b2d]">{error}</p>}
              <button type="submit" disabled={busy} className="inline-flex w-full items-center justify-center rounded-full bg-[var(--color-forest)] px-5 py-3.5 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-[#174f1b] disabled:cursor-wait disabled:opacity-60">{busy ? t.waiting : step === 'details' ? t.sendOtp : t.verify}<ArrowRightIcon className="ml-2 h-4 w-4" /></button>
            </form>
          )}
        </section>
      </div>
    </main>
  );
};

export default JoinUs;
